import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Sprout } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

const Login = () => {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const explicitRedirect = params.get("redirect");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success(t("toast.loginSuccess"));
      // "/" shows the Seller Home page for sellers (see HomeRouter in App.js).
      const roleHome = u.role === "admin" ? "/admin" : "/";
      navigate(explicitRedirect || roleHome);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <BackButton />
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#123A2C] flex items-center justify-center mb-3">
            <Sprout className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#14251F]">{t("auth.loginTitle")}</h1>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-4">
          {error && <div data-testid="login-error" className="text-sm text-[#DC2626] bg-red-50 rounded-lg p-2.5">{error}</div>}
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.identifier")}</label>
            <input
              data-testid="login-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]"
            />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("auth.password")}</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-[#123A2C] text-white font-semibold hover:bg-[#0D2C21] transition-colors disabled:opacity-60"
          >
            {t("auth.loginBtn")}
          </button>
          <p className="text-center text-sm text-[#6B7280]">
            {t("auth.noAccount")}{" "}
            <Link data-testid="go-signup" to="/signup" className="text-[#123A2C] font-semibold">
              {t("auth.createAccount")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
